import { bearerAuth, oauthAuth } from "../standardAuthPatterns";
import { defineProduct, productVariant } from "../types";
import logo from "./logo.svg";

export const webex = defineProduct({
  name: "Webex",
  tagLine: "Video conferencing platform",
  logo,
  tags: ["communication"],
  variants: [
    productVariant({
      identifier: "webex",
      authLabel: "Integration (OAuth)",
      protocol: "http",
      defaultUrlPattern: () => "https://([^/]*\\.)?webexapis\\.com/.*",
      auth: oauthAuth,
      steps: [
        {
          title: "Create a Webex integration",
          items: [
            {
              kind: "markdown",
              content:
                "Use an integration to act on behalf of a Webex user. To send automated messages under a bot’s identity, select **Bot token** instead.\n\nGo to [developer.webex.com/my-apps/new](https://developer.webex.com/my-apps/new) and choose **Create an Integration**. Fill in the **Integration name**, **Icon**, and **Description** — Webex requires these but the values don’t affect anything here.",
            },
          ],
        },
        {
          title: "Set the redirect URI",
          items: [
            {
              kind: "markdown",
              content:
                "Paste the value below into Webex’s mandatory **Redirect URI(s)** field:",
            },
            {
              kind: "copyable",
              label: "Redirect URI",
              value: "{API_URL}/connected-apps/oauth/webex/callback",
            },
          ],
        },
        {
          title: "Choose scopes",
          items: [
            {
              kind: "markdown",
              content:
                "Tick the scopes your workflows will need under **Scopes** in Webex. The `openid` scope is added automatically for the connection test and doesn’t need to be selected in Webex. See the [Webex scope reference](https://developer.webex.com/messaging/docs/api/guides/integrations-and-authorization#scopes) for the full list.",
            },
            {
              kind: "input",
              label: "Scopes",
              required: true,
              placeholder: "spark:rooms_read spark:messages_read …",
              target: { kind: "oauthConnection", field: "scopes" },
            },
          ],
        },
        {
          title: "Save and copy your credentials",
          items: [
            {
              kind: "markdown",
              content:
                "Click **Add Integration** in Webex. The page will show your **Client ID** and **Client Secret** — paste them here.",
            },
            {
              kind: "input",
              label: "Client ID",
              required: true,
              target: { kind: "oauthConnection", field: "clientId" },
            },
            {
              kind: "input",
              label: "Client secret",
              required: true,
              target: { kind: "oauthConnection", field: "clientSecret" },
            },
          ],
        },
        {
          title: "Authorize your Webex account",
          items: [
            {
              kind: "markdown",
              content:
                "Connections authorized without the `openid` scope must be authorized again for the connection test to pass.",
            },
            {
              kind: "oauth",
              dependsOn: [
                { kind: "oauthConnection", field: "clientId" },
                { kind: "oauthConnection", field: "clientSecret" },
                { kind: "oauthConnection", field: "scopes" },
              ],
            },
          ],
        },
      ],
      oauth: {
        grantType: () => "authorization_code",
        authorizationEndpoint: () => "https://webexapis.com/v1/authorize",
        tokenEndpoint: () => "https://webexapis.com/v1/access_token",
        tokenEndpointAuthMethod: () => "client_secret_post",
        clientCredentialsFrom: "db",
        scopes: () => [],
        normalizeAuthorizationRequest: (parameters) => ({
          ...parameters,
          scope: [
            ...new Set([
              ...(parameters.scope?.split(/\s+/).filter(Boolean) ?? []),
              "openid",
            ]),
          ].join(" "),
        }),
        // Only the access token is used; Webex’s unused ID token has a region-specific issuer.
        normalizeTokenResponse: ({ id_token: _idToken, ...tokens }) => tokens,
      },
      connectionTest: {
        protocol: "http",
        request: () => ({
          url: "https://webexapis.com/v1/userinfo",
          expectedStatus: 200,
        }),
      },
    }),
    productVariant({
      identifier: "webexBot",
      authLabel: "Bot token",
      protocol: "http",
      defaultUrlPattern: () => "https://webexapis\\.com/v1/.*",
      auth: (app) => bearerAuth(app.secret.botToken),
      steps: [
        {
          title: "Create a Webex bot",
          items: [
            {
              kind: "markdown",
              content:
                "Use a bot to send automated messages under its own name and avatar. Go to [My Webex Apps](https://developer.webex.com/my-apps/new), choose **Create a Bot**, and enter its name, username, and icon. Click **Add Bot**, then copy the bot’s access token, which Webex displays only once.",
            },
          ],
        },
        {
          title: "Enter your bot token",
          items: [
            {
              kind: "input",
              label: "Bot token",
              required: true,
              target: { kind: "secrets", field: "botToken" },
            },
          ],
        },
        {
          title: "Add the bot to your spaces",
          items: [
            {
              kind: "markdown",
              content:
                "Add the bot to each group space where it should send messages using its bot email address. Bots can read direct messages, but in group spaces they can only read messages that @mention them. To act on behalf of a user, select **Integration (OAuth)** instead. See the [Webex bot guide](https://developer.webex.com/create/docs/bots) for details.",
            },
          ],
        },
      ],
      connectionTest: {
        protocol: "http",
        request: () => "https://webexapis.com/v1/people/me",
      },
      usageHint: () =>
        "Messages are sent as the bot. Add the bot to target group spaces first. When listing group messages, include mentionedPeople=me; bots can only read messages that @mention them in group spaces.",
    }),
    productVariant({
      identifier: "webexPersonalAccessToken",
      authLabel: "Personal access token",
      protocol: "http",
      defaultUrlPattern: () => "https://([^/]*\\.)?webexapis\\.com/.*",
      auth: (app) => bearerAuth(app.secret.accessToken),
      steps: [
        {
          title: "Get your personal access token",
          items: [
            {
              kind: "markdown",
              content:
                "Sign in to the [Webex Developer Portal](https://developer.webex.com/admin/docs/getting-your-personal-access-token) and copy your personal access token from the **Bearer** field. Paste the token below without the `Bearer` prefix.\n\nPersonal access tokens let you try Webex APIs as your user. They expire 12 hours after you sign in to the developer portal and must be replaced manually. For ongoing workflows, select **Integration (OAuth)**.",
            },
            {
              kind: "input",
              label: "Personal access token",
              required: true,
              target: { kind: "secrets", field: "accessToken" },
            },
          ],
        },
      ],
      connectionTest: {
        protocol: "http",
        request: () => ({
          url: "https://webexapis.com/v1/people/me",
          expectedStatus: 200,
        }),
      },
      usageHint: () =>
        "Requests run as your Webex user. Personal access tokens expire 12 hours after signing in to the developer portal and must be replaced manually. Use Integration (OAuth) for ongoing workflows.",
    }),
  ],
});
